/**
 * Bottom sheet that opens when the user taps the Android Auto pill.
 * Two tabs: route POIs along the way (Fricamp spots with satellite preview)
 * and live traffic cameras along the route (tap thumbnail to expand).
 */
import React, { useEffect, useState } from 'react';
import { X, MapPin } from 'lucide-react';
import type { CampingSpot, RouteFollowState, RouteFollowPoi, SvvCamera } from '../types';
import { fetchCamerasNearRoute } from '../services/svvCameraService';
import { getDistance } from '../services/geometryUtils';

interface AndroidAutoRouteSheetProps {
  open: boolean;
  followRouteState: RouteFollowState | null;
  progressM: number | null;
  isOffRoute: boolean;
  spots?: CampingSpot[];
  onClose: () => void;
  onStop: () => void;
  onSelectSpot?: (spot: CampingSpot) => void;
}

const formatDist = (m: number): string =>
  m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;

const formatDuration = (s: number): string => {
  if (s < 60) return `${Math.round(s)} s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s - h * 3600) / 60);
  return `${h} t ${m} min`;
};

const AVG_SPEED_MS = 50 / 3.6;

function satelliteTile(lat: number, lon: number, span = 0.0015): string {
  return `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/export?bbox=${lon - span},${lat - span},${lon + span},${lat + span}&bboxSR=4326&size=200,200&f=image`;
}

interface CameraWithDistance extends SvvCamera {
  distanceAlongRoute: number;
}

export const AndroidAutoRouteSheet: React.FC<AndroidAutoRouteSheetProps> = ({
  open,
  followRouteState,
  progressM,
  isOffRoute,
  spots = [],
  onClose,
  onStop,
  onSelectSpot,
}) => {
  const [tab, setTab] = useState<'pois' | 'cameras'>('pois');
  const [cameras, setCameras] = useState<CameraWithDistance[]>([]);
  const [loadingCameras, setLoadingCameras] = useState(false);
  const [expandedCamera, setExpandedCamera] = useState<SvvCamera | null>(null);

  // Fetch cameras when sheet opens with an active route
  useEffect(() => {
    if (!open || !followRouteState?.routeCoords) return;
    setLoadingCameras(true);
    fetchCamerasNearRoute(followRouteState.routeCoords, 2000)
      .then(cams => {
        // Map each camera to its distance along the route (via nearest-point lookup)
        const withDist: CameraWithDistance[] = cams.map(cam => {
          let bestDist = Infinity;
          let bestIdx = 0;
          followRouteState.routeCoords.forEach((c, i) => {
            const d = getDistance(cam.latitude, cam.longitude, c.latitude, c.longitude);
            if (d < bestDist) { bestDist = d; bestIdx = i; }
          });
          // Approximate distance along route: cumulative distance up to nearest index
          let cumul = 0;
          for (let i = 0; i < bestIdx; i++) {
            const a = followRouteState.routeCoords[i];
            const b = followRouteState.routeCoords[i + 1];
            cumul += getDistance(a.latitude, a.longitude, b.latitude, b.longitude);
          }
          return { ...cam, distanceAlongRoute: cumul };
        });
        withDist.sort((a, b) => a.distanceAlongRoute - b.distanceAlongRoute);
        setCameras(withDist);
      })
      .catch(err => console.warn('[AARouteSheet] camera fetch failed:', err))
      .finally(() => setLoadingCameras(false));
  }, [open, followRouteState?.routeId]);

  if (!open || !followRouteState) return null;

  const totalDist = followRouteState.routeTotalDistanceM;
  const totalDur = followRouteState.routeTotalDurationS ?? (totalDist / AVG_SPEED_MS);
  const progress = progressM ?? 0;
  const remaining = Math.max(0, totalDist - progress);
  const remainingDur = totalDist > 0 ? (totalDur * remaining) / totalDist : 0;
  const progressPct = totalDist > 0 ? Math.min(100, (progress / totalDist) * 100) : 0;

  const upcomingPois = (followRouteState.pois ?? [])
    .filter((p: RouteFollowPoi) => p.distanceAlongRoute > progress)
    .slice(0, 20);
  const upcomingCameras = cameras.filter(c => c.distanceAlongRoute > progress);

  const findSpotForPoi = (poi: RouteFollowPoi): CampingSpot | undefined =>
    spots.find(s => s.id === poi.id);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0f172a',
          color: '#e0e7ff',
          width: '100%',
          maxWidth: 480,
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: 20,
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            width: 40, height: 4, borderRadius: 2,
            background: 'rgba(255,255,255,0.2)', margin: '0 auto 12px',
          }}
        />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'rgba(59,130,246,0.25)', display: 'grid', placeItems: 'center',
            fontSize: 22, flexShrink: 0,
          }}>🚗</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: '#93c5fd', letterSpacing: 0.4, fontWeight: 600 }}>
              NAVIGERER I BILEN
            </div>
            <div style={{
              fontSize: 16, fontWeight: 700,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              marginTop: 2,
            }}>
              {followRouteState.routeName || 'Fricamp-rute'}
            </div>
          </div>
          <button type="button" onClick={onClose} style={{
            background: 'transparent', border: 'none', color: '#94a3b8',
            cursor: 'pointer', padding: 4,
          }} aria-label="Lukk">
            <X size={20} />
          </button>
        </div>

        {/* Progress */}
        <div style={{ marginBottom: 16 }}>
          <div style={{
            width: '100%', height: 6, background: 'rgba(255,255,255,0.1)',
            borderRadius: 3, overflow: 'hidden',
          }}>
            <div style={{
              width: `${progressPct}%`, height: '100%',
              background: isOffRoute ? '#ef4444' : '#22c55e',
              transition: 'width 0.4s ease',
            }} />
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', marginTop: 6,
            fontSize: 12, color: '#cbd5e1',
          }}>
            <span>{formatDist(progress)} kjørt</span>
            <span>{formatDist(remaining)} igjen · {formatDuration(remainingDur)}</span>
          </div>
        </div>

        {isOffRoute && (
          <div style={{
            background: 'rgba(239,68,68,0.15)',
            border: '1px solid rgba(239,68,68,0.35)',
            color: '#fca5a5', padding: 10, borderRadius: 10,
            fontSize: 13, marginBottom: 16,
          }}>⚠ Utenfor ruten — bilen omberegner</div>
        )}

        {/* Tab bar */}
        <div style={{
          display: 'flex', gap: 4, padding: 3, borderRadius: 999,
          background: 'rgba(255,255,255,0.06)', marginBottom: 12,
        }}>
          {([
            { id: 'pois' as const, label: `Plasser (${upcomingPois.length})` },
            { id: 'cameras' as const, label: `Kamera (${upcomingCameras.length})` },
          ]).map(t => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              style={{
                flex: 1, padding: '8px 12px', border: 'none', borderRadius: 999,
                background: tab === t.id ? '#3b82f6' : 'transparent',
                color: tab === t.id ? '#fff' : '#cbd5e1',
                fontWeight: 600, fontSize: 13, cursor: 'pointer',
                transition: 'background 0.2s',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* POI tab */}
        {tab === 'pois' && (
          <div style={{ marginBottom: 12 }}>
            {upcomingPois.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#64748b', padding: 24, fontSize: 13 }}>
                Ingen Fricamp-plasser foran på ruten
              </div>
            ) : upcomingPois.map(poi => {
              const distToPoi = poi.distanceAlongRoute - progress;
              const spot = findSpotForPoi(poi);
              const score = spot?.score ?? spot?.aiResult?.score;
              return (
                <button
                  key={poi.id}
                  type="button"
                  onClick={() => { if (spot && onSelectSpot) { onSelectSpot(spot); onClose(); } }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    width: '100%', textAlign: 'left',
                    padding: 10, marginBottom: 6,
                    background: 'rgba(255,255,255,0.04)',
                    border: 'none', borderRadius: 12,
                    color: '#e0e7ff', cursor: spot ? 'pointer' : 'default',
                  }}
                >
                  <div style={{
                    width: 56, height: 56, borderRadius: 8, overflow: 'hidden',
                    background: '#1e293b', flexShrink: 0, position: 'relative',
                  }}>
                    <img
                      src={satelliteTile(poi.location.latitude, poi.location.longitude)}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      loading="lazy"
                    />
                    <div style={{
                      position: 'absolute', inset: 0,
                      display: 'grid', placeItems: 'center',
                    }}>
                      <MapPin size={20} style={{ color: '#22c55e', filter: 'drop-shadow(0 0 2px #000)' }} fill="#22c55e" />
                    </div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 14, fontWeight: 600,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {poi.name || 'Fricamp-plass'}
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                      {poi.category}{score ? ` · ★ ${score.toFixed(1)}` : ''} · {formatDist(distToPoi)} videre
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Camera tab */}
        {tab === 'cameras' && (
          <div style={{ marginBottom: 12 }}>
            {loadingCameras ? (
              <div style={{ textAlign: 'center', color: '#64748b', padding: 24, fontSize: 13 }}>
                Henter trafikkamera…
              </div>
            ) : upcomingCameras.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#64748b', padding: 24, fontSize: 13 }}>
                Ingen trafikkamera foran på ruten
              </div>
            ) : upcomingCameras.map(cam => {
              const distToCam = cam.distanceAlongRoute - progress;
              return (
                <button
                  key={cam.id}
                  type="button"
                  onClick={() => setExpandedCamera(cam)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: 0, marginBottom: 10,
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    position: 'relative',
                  }}
                >
                  <div style={{
                    position: 'relative',
                    borderRadius: 12, overflow: 'hidden',
                    background: '#1e293b',
                  }}>
                    <img
                      src={cam.image_url}
                      alt={cam.name}
                      style={{
                        width: '100%', height: 'auto', display: 'block',
                        aspectRatio: '16/9', objectFit: 'cover',
                      }}
                      loading="lazy"
                    />
                    {/* Overlay: name + distance bottom-left */}
                    <div style={{
                      position: 'absolute', left: 0, right: 0, bottom: 0,
                      padding: '8px 10px',
                      background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.85) 100%)',
                      color: '#fff',
                    }}>
                      <div style={{
                        fontSize: 13, fontWeight: 700,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>{cam.name}</div>
                      <div style={{ fontSize: 11, color: '#cbd5e1', marginTop: 2 }}>
                        {cam.road_name ? `${cam.road_name} · ` : ''}{formatDist(distToCam)} videre
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Stop button */}
        <button type="button" onClick={() => { onStop(); onClose(); }} style={{
          width: '100%', background: '#dc2626', color: '#fff', border: 'none',
          padding: '12px 16px', borderRadius: 12,
          fontWeight: 700, fontSize: 14, cursor: 'pointer',
        }}>Stopp navigasjon</button>

        <p style={{
          fontSize: 11, color: '#64748b', textAlign: 'center',
          marginTop: 12, marginBottom: 0,
        }}>Bilen styrer turn-by-turn — telefonen er i bla-modus.</p>
      </div>

      {/* Fullscreen camera modal */}
      {expandedCamera && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 10001,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setExpandedCamera(null)}
        >
          <div style={{ position: 'relative', maxWidth: '100%', maxHeight: '100%' }}>
            <img
              src={expandedCamera.image_url}
              alt={expandedCamera.name}
              style={{
                maxWidth: '100%', maxHeight: '90vh',
                display: 'block', borderRadius: 8,
              }}
            />
            <div style={{
              position: 'absolute', left: 0, right: 0, bottom: 0,
              padding: '12px 16px',
              background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.9) 100%)',
              color: '#fff',
              borderBottomLeftRadius: 8, borderBottomRightRadius: 8,
            }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{expandedCamera.name}</div>
              {expandedCamera.road_name && (
                <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 2 }}>{expandedCamera.road_name}</div>
              )}
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setExpandedCamera(null); }}
              style={{
                position: 'absolute', top: 8, right: 8,
                background: 'rgba(0,0,0,0.7)', color: '#fff',
                border: 'none', borderRadius: '50%',
                width: 36, height: 36, cursor: 'pointer',
                display: 'grid', placeItems: 'center',
              }}
              aria-label="Lukk"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AndroidAutoRouteSheet;
